import React, { useEffect, useRef, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { X, FileText, Upload, Trash2, ExternalLink, Eye, EyeOff, RefreshCw, Loader2 } from "lucide-react";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const MAX_MB = 10;

export default function TermReportsDrawer({ student, can, onClose }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({ terms: ["Term 1", "Term 2", "Term 3"], current_session: "" });
  const canManage = can("manage_term_reports");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: c }] = await Promise.all([
        adminApi.get(`/admin/students/${student.id}/term-reports`),
        adminApi.get("/admin/term-reports/config"),
      ]);
      setReports(r);
      setConfig(c);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load reports");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [student.id]);

  const openPdf = async (r) => {
    try {
      const res = await adminApi.get(`/admin/term-reports/${r.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't open PDF");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this term report? This can't be undone.")) return;
    try {
      await adminApi.delete(`/admin/term-reports/${id}`);
      toast.success("Report deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const togglePublish = async (r) => {
    try {
      await adminApi.patch(`/admin/term-reports/${r.id}`, { is_published: !r.is_published });
      toast.success(r.is_published ? "Report unpublished" : "Report published");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-[#0F1B47]/40" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#FDFDF9] shadow-2xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={`term-reports-drawer-${student.id}`}>
        {/* Header */}
        <div className="sticky top-0 bg-[#FDFDF9]/95 backdrop-blur px-6 py-4 border-b border-[#0F1B47]/8 flex items-start justify-between">
          <div>
            <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-[#648DE5]">{student.name} · {student.admission_id}</div>
            <div className="font-display text-2xl text-[#0F1B47] font-medium mt-0.5">Term reports</div>
          </div>
          <button onClick={onClose} className="text-[#0F1B47] p-1.5 rounded-full hover:bg-[#0F1B47]/5" data-testid="term-reports-close"><X size={18} /></button>
        </div>

        {/* Existing reports */}
        <div className="px-6 py-5">
          {loading ? (
            <div className="text-center text-[#A0AEC0] text-xs py-8"><Loader2 className="inline animate-spin" size={14} /></div>
          ) : reports.length === 0 ? (
            <div className="text-center text-[#A0AEC0] text-xs py-6 italic">No term reports yet.</div>
          ) : (
            <div className="space-y-2 mb-4" data-testid="term-reports-list">
              {reports.map((r) => (
                <div key={r.id} className="flex items-center gap-3 bg-[#F5F5F0] rounded-2xl p-3">
                  <div className="w-9 h-11 rounded bg-gradient-to-b from-[#FED7D7] to-[#F97878] text-white text-[10px] font-bold grid place-items-center flex-shrink-0">PDF</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#0F1B47] text-sm">{r.term} · {r.session}</div>
                    <div className="text-[11px] text-[#718096]">
                      {new Date(r.uploaded_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })} · {(r.size_bytes / 1024).toFixed(0)} KB
                    </div>
                    {r.notes && <div className="text-[11px] text-[#4A5568] italic mt-0.5">{r.notes}</div>}
                  </div>
                  <span className={`text-[9px] font-bold tracking-[0.12em] uppercase px-2 py-0.5 rounded-full ${r.is_published ? "bg-[#E6FFFA] text-[#2F855A]" : "bg-[#FFF5B7] text-[#B7791F]"}`}>
                    {r.is_published ? "Published" : "Draft"}
                  </span>
                  <button onClick={() => openPdf(r)} className="text-[11px] font-semibold text-[#0F1B47] p-1.5 rounded-lg hover:bg-[#0F1B47]/10" title="Open PDF" data-testid={`term-report-view-${r.id}`}>
                    <ExternalLink size={13} />
                  </button>
                  {canManage && (
                    <>
                      <button onClick={() => togglePublish(r)} className="text-[11px] font-semibold text-[#0F1B47] p-1.5 rounded-lg hover:bg-[#0F1B47]/10" title={r.is_published ? "Unpublish" : "Publish"} data-testid={`term-report-toggle-${r.id}`}>
                        {r.is_published ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                      <button onClick={() => remove(r.id)} className="text-[11px] font-semibold text-[#C53030] p-1.5 rounded-lg hover:bg-[#FED7D7]" title="Delete" data-testid={`term-report-delete-${r.id}`}>
                        <Trash2 size={13} />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Uploader */}
          {canManage && (
            <UploaderForm student={student} config={config} existing={reports} onDone={load} />
          )}
          {!canManage && (
            <div className="text-[11px] text-[#A0AEC0] italic mt-2">You need <b>manage_term_reports</b> to add or remove reports.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function UploaderForm({ student, config, existing, onDone }) {
  const [session, setSession] = useState(config.current_session);
  const [term, setTerm] = useState(config.terms[0]);
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [publish, setPublish] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => { setSession(config.current_session); }, [config.current_session]);

  const willReplace = existing.some((r) => r.session === session && r.term === term);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) {
      return toast.error("Only PDF files are accepted");
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      return toast.error(`File is ${(f.size / (1024 * 1024)).toFixed(1)} MB — max ${MAX_MB} MB`);
    }
    setFile(f);
  };

  const submit = async () => {
    if (!file) return toast.error("Pick a PDF file first");
    if (!session.trim()) return toast.error("Enter a session");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("session", session.trim());
      fd.append("term", term);
      fd.append("notes", notes);
      fd.append("is_published", String(publish));
      fd.append("file", file);
      await adminApi.post(`/admin/students/${student.id}/term-reports`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      toast.success(willReplace ? "Report replaced" : "Report uploaded");
      setFile(null); setNotes(""); if (fileInput.current) fileInput.current.value = "";
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="border-t border-dashed border-[#0F1B47]/15 pt-4">
      <div className="text-[11px] font-bold tracking-[0.14em] uppercase text-[#648DE5] mb-3">Upload a new report</div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-1">Session</div>
          <input value={session} onChange={(e) => setSession(e.target.value)} className="w-full text-sm bg-[#F5F5F0] rounded-xl px-3 py-2 border border-[#0F1B47]/8" placeholder="2026-27" data-testid="term-report-session" />
        </div>
        <div>
          <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-1">Term</div>
          <select value={term} onChange={(e) => setTerm(e.target.value)} className="w-full text-sm bg-[#F5F5F0] rounded-xl px-3 py-2 border border-[#0F1B47]/8" data-testid="term-report-term">
            {config.terms.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <input ref={fileInput} type="file" accept="application/pdf,.pdf" onChange={pickFile} className="hidden" data-testid="term-report-file-input" />
      <button type="button" onClick={() => fileInput.current?.click()} className="w-full border-2 border-dashed border-[#0F1B47]/20 rounded-2xl px-4 py-5 text-center hover:border-[#0F1B47]/40 transition" data-testid="term-report-pick-file">
        {file ? (
          <div className="text-sm text-[#0F1B47]">
            <FileText size={18} className="inline mr-1" /> <b>{file.name}</b>
            <div className="text-[11px] text-[#718096] mt-1">{(file.size / 1024).toFixed(0)} KB</div>
          </div>
        ) : (
          <>
            <div className="font-display text-lg text-[#0F1B47]">Drop PDF here</div>
            <div className="text-[11px] text-[#718096] mt-1">or click to browse · Max {MAX_MB} MB · PDF only</div>
          </>
        )}
      </button>

      <div className="mt-3">
        <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-1">Notes for parent (optional)</div>
        <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder='e.g. "Please review the literacy section on page 3"' className="w-full text-sm bg-[#F5F5F0] rounded-xl px-3 py-2 border border-[#0F1B47]/8" data-testid="term-report-notes" />
      </div>

      <label className="flex items-center gap-2 mt-3 text-[13px] cursor-pointer">
        <input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)} data-testid="term-report-publish" />
        Publish to parent immediately
      </label>

      {willReplace && (
        <div className="mt-3 text-[11px] text-[#B7791F] bg-[#FFF5B7] rounded-lg px-3 py-2">
          A report for {term} · {session} already exists — uploading will replace it.
        </div>
      )}

      <button onClick={submit} disabled={saving || !file} className="w-full mt-4 bg-[#0F1B47] text-[#FDFDF9] rounded-full py-3 font-bold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="term-report-upload-btn">
        <Upload size={14} /> {saving ? "Uploading…" : willReplace ? "Replace report" : "Upload & save"}
      </button>
    </div>
  );
}
